import { Component, Vue, Prop, PropSync, Watch } from 'vue-property-decorator';
import ObjectDisplayingModeEnum from "@/enums/ObjectDisplayingModeEnum";
import FormDraft from "@/types/fields/FormDraft";
import BaseFormDraft from '@/types/fields/BaseFormDraft';
import ISection from '@/interfaces/ISection';

/** Classe commune à (quasi) tous les formulaires de la fiche objet (ObjectDisplaying)
 * @template BFD Type héritant de BaseFormDraft à utiliser sur la prop Form. Par défaut : type FormDraft.
 */
@Component
export class BaseObjectForm<BFD extends BaseFormDraft<ISection<any, any>> = FormDraft> extends Vue {
	/** Indique si les champs du formulaire sont éditables ou en mode consultation */
	@Prop()
	public ConsultMode!: ObjectDisplayingModeEnum;

    /** Droit disponible pour l'utilisateur en cours */
    @Prop()
    public Action!: string;

	/** Variant de couleur à appliquer sur le composant */
	@Prop({default: () => "secondary"})
	public Variant!: string;

	@Prop({default: () => ""})
	public NodeId!: string;

	@Prop({default: () => ""})
	public ObjectType!: string;

	/** Données de formulaire à charger */
	@PropSync('Form')
	public form!: BFD;

	/** Anciennes données de formulaire (pour comparaison et détecter changement) */
	private oldForm!: BFD;

	/** Indique si le watcher sur la prop Form est actif dès le created ou non */
	protected immediateFormWatcher: boolean = true;

	/** 
	 * Pointeur vers fonction pour désactiver watcher sur l'instance de form 
	 * @see https://vuejs.org/guide/essentials/watchers#stopping-a-watcher
	 **/
	private unwatchForm: () => void = undefined;

	mounted() {
		console.debug('BaseObjectForm - mounted() - this.form : %O -  this.oldForm : %O', this.form, this.oldForm);

		/** 
		 * Déclare un watcher qui track les modifications sur le formulaire en cours afin de l'indiquer à l'utilisateur
		 * @description oldVal et newVal en paramètres du watcher ne sont pas utilissé car il s'agit de référence et l'objet est donc le même. Les modifications sont donc trackés en stockant une copie de l'ancienne valeur dans la variable locale oldForm.
		 * @see https://stackoverflow.com/questions/66430678/vue-do-watchers-on-deep-nested-objects-log-oldval-and-newval
		 * @see https://v2.vuejs.org/v2/api/#vm-watch
		 */
		this.unwatchForm = this.$watch('form', () => {
			if (this.form && Object.keys(this.form).length > 0) {
				console.debug('BaseObjectForm - mounted() 1 - this.form : %O -  this.oldForm : %O', this.form, this.oldForm);
				if (this.oldForm && this.oldForm.IsUpdatesTracked && this.form.IsUpdatesTracked && JSON.stringify(this.form) != JSON.stringify(this.oldForm)) {
					console.debug('BaseObjectForm - mounted() 2 - this.form : %s ||||  this.oldForm : %s', JSON.stringify(this.form), JSON.stringify(this.oldForm));
					this.$store.dispatch('ObjectStore/AddUpdatedForm', this.form.Name);
				}
	
				/** Copie l'objet pour pouvoir le comparer plus tard en cas de changement dans le formulaire */
				this.oldForm = JSON.parse(JSON.stringify(this.form));
			}
		}, { deep: true, immediate: this.immediateFormWatcher });
	}

	private unloadObjectForm() {
		// Réinitialisation de l'ancien formulaire
		this.oldForm = undefined;

		/** Désactive le watcher sur l'instance de FilterTree () sur le noeud ou les pins sélectionnés 
		 * @see https://vuejs.org/guide/essentials/watchers#stopping-a-watcher */
		if (this.unwatchForm)
			this.unwatchForm();
	}

	beforeDestroy() {
		this.unloadObjectForm();
	}
}